import React, { FC } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { activeEnv } from '../../data/constants';
import { appLocales } from '../../i18n';

type Props = {
  locale: string;
  pathname: string;
};

// Pages that are only available in English
const ENGLISH_ONLY_PATHS = ['/wallet/', '/wallet/provider/', '/wallet/thank-you/'];

/**
 * Generates the base path by stripping locale prefixes from the pathname.
 * For English (default), the pathname IS the base path.
 * For other locales, strip the locale prefix (e.g., /es/bitcoin/ -> /bitcoin/).
 */
const getBasePath = (pathname: string): string => {
  const matchedLocale = Object.keys(appLocales).find(localeKey => {
    const localeData = appLocales[localeKey];
    if (localeData.default) return false;
    const prefix = `/${localeData.path}/`;
    return pathname.startsWith(prefix) || pathname === `/${localeData.path}/`;
  });

  if (matchedLocale) {
    const prefix = `/${appLocales[matchedLocale].path}/`;
    if (pathname === prefix) return '/';
    return pathname.slice(prefix.length - 1) || '/';
  }

  return pathname;
};

/**
 * Generates the full URL for a specific locale.
 * English (default) uses the base path directly.
 * Other locales prepend the locale prefix.
 */
const getLocaleUrl = (
  siteUrl: string,
  basePath: string,
  localeKey: string,
): string => {
  const localeData = appLocales[localeKey];
  if (localeData.default) {
    return `${siteUrl}${basePath}`;
  }
  // For non-default locales, prepend the locale path
  // basePath starts with /, so we need to handle it correctly
  if (basePath === '/') {
    return `${siteUrl}/${localeData.path}/`;
  }
  return `${siteUrl}/${localeData.path}${basePath}`;
};

/**
 * Checks if a path is an English-only page.
 */
const isEnglishOnlyPath = (basePath: string): boolean => {
  return ENGLISH_ONLY_PATHS.some(path => basePath.startsWith(path));
};

const Head: FC<Props & Queries.MetaFragment> = props => {
  const { meta, locale, pathname } = props;
  const { title, description, image: rawImage, twitter, schema } = meta || {};
  const { publicURL: image } = rawImage || {};

  const {
    site: {
      siteMetadata: { siteUrl },
    },
  } = useStaticQuery(graphql`
    query SiteData {
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `);

  // Generate hreflang tags
  const basePath = getBasePath(pathname);
  const isEnglishOnly = isEnglishOnlyPath(basePath);
  const defaultLocaleUrl = getLocaleUrl(siteUrl, basePath, 'en');

  // Generate hreflang links for all available locales
  const hreflangTags = isEnglishOnly
    ? [{ lang: 'en', url: defaultLocaleUrl }]
    : Object.keys(appLocales).map(localeKey => ({
        lang: localeKey,
        url: getLocaleUrl(siteUrl, basePath, localeKey),
      }));

  return (
    <>
      <html key="html" lang={locale} />

      <title key="title">{title}</title>
      <meta key="description" name="description" content={`${description}`} />

      <meta key="og:title" property="og:title" content={`${title}`} />
      <meta
        key="og:description"
        property="og:description"
        content={`${description}`}
      />
      <meta key="og:image" property="og:image" content={`${siteUrl}${image}`} />
      <meta
        key="og:image_secure"
        property="og:image:secure_url"
        content={`${siteUrl}${image}`}
      />
      <meta key="og:image_width" property="og:image:width" content="1200" />
      <meta key="og:image_height" property="og:image:height" content="630" />

      <meta
        key="twitter:card"
        name="twitter:card"
        content="summary_large_image"
      />
      <meta key="twitter:site" name="twitter:site" content={`@${twitter}`} />
      <meta key="twitter:title" name="twitter:title" content={`${title}`} />
      <meta
        key="twitter:description"
        name="twitter:description"
        content={`${description}`}
      />
      <meta
        key="twitter:image"
        name="twitter:image"
        content={`${siteUrl}${image}`}
      />

      <link key="canonical" rel="canonical" href={`${siteUrl}${pathname}`} />

      {/* Hreflang tags for international SEO */}
      {hreflangTags.map(({ lang, url }) => (
        <link
          key={`hreflang-${lang}`}
          rel="alternate"
          hrefLang={lang}
          href={url}
        />
      ))}
      {/* x-default for users without a language match */}
      <link
        key="hreflang-x-default"
        rel="alternate"
        hrefLang="x-default"
        href={defaultLocaleUrl}
      />

      {activeEnv !== 'production' && <meta name="robots" content="noindex" />}

      {/* JSON-LD Structured Data */}
      {schema && Object.keys(schema).length > 0 && (
        <script
          key="json-ld"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      )}
    </>
  );
};

export const query = graphql`
  fragment Meta on Json {
    meta {
      title
      description
      image {
        publicURL
      }
      twitter
      schema
    }
  }
`;

export default Head;
